import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Component, OnInit, ViewChild } from '@angular/core';
import { LazyLoadEvent, CheckboxModule, Dialog } from 'primeng';
import { IERegion } from '../../interfaces/eregion';
import { IERegionFilter } from '../../interfaces/eregion-filter';
import { NoEmptyString } from '../../interfaces/no-empty-string.validator';
import { IPageResult } from '../../interfaces/page-result';
import { EngineiusRegionService, EngineiusAreaService, OrganisationsService } from '../../services';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'eregions',
  templateUrl: './manage-eregions.component.html',
  styleUrls: ['./manage-eregions.component.scss'],
})
export class ERegionsComponent implements OnInit {
  public page: IPageResult<IERegion> = {
    items: [],
    total: 0,
  };

  public event: any;
  public filter: Subject<IERegionFilter>;
  public filterVal: IERegionFilter;
  public isLoaded: boolean = false;
  public displayDialog: boolean = false;
  public region: IERegion;
  public newregion: boolean = false;
  public filterString: string;
  public newERegionForm: UntypedFormGroup;
  public name: UntypedFormControl;
  public description: UntypedFormControl;
  public isActive: UntypedFormControl;
  public ukcodes: string[];
  public londoncodes: string[];
  public postcodesDialogVisible: boolean = false;
  public dlgPostcodesUK: string[] = [];
  public dlgPostcodesLondon: string[] = [];
  public selectedRegionId: number;
  public selectedItem: any;
  eAreas: { value: number; label: string }[] = [];
  public engineiusAreaId: UntypedFormControl;

  @ViewChild('postcodeDialog') postcodeDialog: Dialog;

  constructor(
    private EngineiusRegionService: EngineiusRegionService,
    private EngineiusAreaService: EngineiusAreaService,
    private formBuilder: UntypedFormBuilder,
    private orgService: OrganisationsService,
  ) {}

  ngOnInit() {
    this.filter = new Subject<IERegionFilter>();

    // subscribe to filter changes with a debounce time
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    // set default filter
    this.filter.next({ first: 0, rows: 10 });

    // set formGroup instance

    this.newERegionForm = this.formBuilder.group({
      id: new UntypedFormControl(''),
      name: new UntypedFormControl(
        '',
        Validators.compose([Validators.required, Validators.maxLength(255), NoEmptyString]),
      ),
      engineiusAreaId: new UntypedFormControl(''),
    });

    this.loadEAreas();
  }

  load(filter: IERegionFilter) {
    this.EngineiusRegionService.getPage(filter).then((result) => {
      this.page.items = result.items;
      this.page.total = result.total;
      this.isLoaded = true;
    });
  }

  loadEAreas() {
    this.EngineiusAreaService.getPage({ rows: 100 }).then((result) => {
      this.eAreas = result.items
        .map((x, y) => {
          return { value: x.id, label: x.name };
        })
        .sort((a, b) => {
          return a.label.localeCompare(b.label);
        });

      this.isLoaded = true;
    });
  }

  areaLookup(id) {
    if (id == null) {
      return '';
    }

    return this.eAreas.filter((x) => x.value == id).map((x) => x.label)[0];
  }

  showDialogToAdd() {
    this.newregion = true;
    this.region = {
      id: 0,
      name: '',
      engineiusAreaId: null,
    };
    this.resetFormValues(this.region);
    this.displayDialog = true;
  }

  save() {
    let fv = this.newERegionForm.value;
    let newRegion = fv.id == 0;

    if (fv.name !== '') {
      this.EngineiusRegionService.createregion(fv, newRegion).then((res) =>
        this.load(this.filterVal),
      );
    }
    this.displayDialog = false;
  }

  close() {
    this.displayDialog = false;
  }

  resetFormValues(formValues) {
    this.newERegionForm.patchValue({
      id: formValues.id,
      name: formValues.name,
      engineiusAreaId: formValues.engineiusAreaId,
    });
  }

  onRowSelect(event) {
    this.event = event;
  }

  onRowDblclick(item) {
    this.newregion = false;
    this.resetFormValues(this.selectedItem);
    this.displayDialog = true;
  }

  onEdit() {
    if (this.event !== undefined) {
      this.onRowDblclick(this.event);
    }
  }

  cloneregion(c: IERegion): IERegion {
    let region = { id: 0, name: '' };
    for (let prop in c) {
      region[prop] = c[prop];
    }
    return region;
  }

  loadRegionPostcodes(id: number) {
    this.orgService.geteregionpostcodes(id).then((result) => {
      this.ukcodes = [];
      this.londoncodes = [];

      result.forEach((o) => {
        if (o.isLondon) {
          this.londoncodes.push(o.postcode);
        } else {
          this.ukcodes.push(o.postcode);
        }
      });
    });
  }

  savePostcodes() {
    var postcodes: any[] = [];
    this.ukcodes.forEach((o) => postcodes.push({ postcode: o, isLondon: false }));
    this.londoncodes.forEach((o) => postcodes.push({ postcode: o, isLondon: true }));

    //console.log("Saving postcodes, UK: ", this.ukcodes);
    //console.log("Saving postcodes, London: ", this.londoncodes);

    this.orgService.updateERegionPostcodes(this.selectedRegionId, postcodes);
  }

  dlgSavePostcodes() {
    this.savePostcodes();
    this.postcodesDialogVisible = false;
  }

  onEditPostcodes() {
    //console.log("Loading eregion postcodes: ", this.event);

    if (this.event !== undefined) {
      this.loadRegionPostcodes(this.event.data.id);
      this.selectedRegionId = this.event.data.id;
      this.postcodesDialogVisible = true;

      window.setTimeout(() => {
        this.postcodeDialog.maximize();
      }, 100);
    }
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    // map grid lazy load event to filter
    var eventString = JSON.stringify(event);
    if (eventString === this.filterString) return;
    console.log(event);
    this.filter.next(event);
  }

  onUKCodesChanged(codes: string[]) {
    this.ukcodes = codes;
  }

  onLondonCodesChanged(codes: string[]) {
    this.londoncodes = codes;
  }
}
